import { environment } from "@environment";
import type { TFlag, TFlagConfig } from "./types";

const CACHE_KEY = "sd-feature-flags";
const DEFAULT_TIMEOUT_MS = 1500;
const EMPTY_CONFIG: TFlagConfig = { version: 0, flags: {} };

export const validateConfig = (raw: unknown): TFlagConfig => {
  if (!raw || typeof raw !== "object") {
    throw new Error("feature-flags: config is not an object");
  }
  const candidate = raw as { version?: unknown; flags?: unknown };
  if (!candidate.flags || typeof candidate.flags !== "object") {
    throw new Error("feature-flags: missing flags object");
  }
  const flags = candidate.flags as Record<string, TFlag>;
  for (const key of Object.keys(flags)) {
    const flag = flags[key];
    if (!flag || typeof flag.name !== "string") {
      throw new Error(`feature-flags: flag "${key}" has no name`);
    }
  }
  return {
    version: typeof candidate.version === "number" ? candidate.version : 0,
    flags,
  };
};

export const readCachedConfig = (): TFlagConfig | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return validateConfig(JSON.parse(raw));
  } catch {
    return null;
  }
};

const writeCache = (config: TFlagConfig): void => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(config));
  } catch {
    // ignore quota / private-mode errors — caching is best-effort
  }
};

export const fetchFlagConfigStrict = async (
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<TFlagConfig> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(environment.featureFlagsCdnUrl, {
      signal: controller.signal,
      cache: "no-cache",
    });
    // 404 = config has never been published yet — a valid empty bootstrap state.
    if (response.status === 404) return { version: 0, flags: {} };
    if (!response.ok) {
      throw new Error(`feature-flags: HTTP ${response.status}`);
    }
    return validateConfig(await response.json());
  } finally {
    clearTimeout(timer);
  }
};

export const fetchFlagConfig = async (
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<TFlagConfig> => {
  const cached = readCachedConfig();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(environment.featureFlagsCdnUrl, {
      signal: controller.signal,
      cache: "no-cache",
    });
    // 404 = config has never been published yet — a valid empty bootstrap
    // state, not an error to log on every cold boot.
    if (response.status === 404) return EMPTY_CONFIG;
    if (!response.ok) {
      throw new Error(`feature-flags: HTTP ${response.status}`);
    }
    const config = validateConfig(await response.json());
    writeCache(config);
    return config;
  } catch (error) {
    console.error("[feature-flags] fetch failed, using fallback", error);
    return cached ?? EMPTY_CONFIG;
  } finally {
    clearTimeout(timer);
  }
};
